import React from 'react';
import { Check, AlertCircle } from 'lucide-react';
import { STEP_LABELS } from './types';

interface WizardTabsProps {
  currentStep: number;
  completedSteps: number[];
  errorSteps?: number[];
  onStepClick: (step: number) => void;
}

export default function WizardTabs({
  currentStep,
  completedSteps,
  errorSteps = [],
  onStepClick,
}: WizardTabsProps) {
  const maxReached = Math.max(...completedSteps, 0) + 1;

  return (
    <div className="shrink-0 border-b border-gray-200 bg-white">
      <nav className="flex overflow-x-auto scrollbar-hide px-6" aria-label="Wizard steps">
        {STEP_LABELS.map((label, idx) => {
          const step = idx + 1;
          const isActive = step === currentStep;
          const isCompleted = completedSteps.includes(step);
          const hasError = errorSteps.includes(step);
          const canNavigate = isCompleted || step <= maxReached;

          return (
            <button
              key={step}
              onClick={() => canNavigate && onStepClick(step)}
              disabled={!canNavigate}
              aria-current={isActive ? 'step' : undefined}
              className={[
                'relative flex items-center gap-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors shrink-0',
                isActive
                  ? 'border-orange-500 text-orange-600'
                  : isCompleted
                    ? 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    : canNavigate
                      ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      : 'border-transparent text-gray-300 cursor-not-allowed',
              ].join(' ')}
            >
              {/* Step indicator */}
              <span
                className={[
                  'flex items-center justify-center h-5 w-5 rounded-full text-xs font-semibold shrink-0',
                  isActive
                    ? 'bg-orange-600 text-white'
                    : hasError
                      ? 'bg-red-500 text-white'
                      : isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500',
                ].join(' ')}
              >
                {hasError ? (
                  <AlertCircle className="h-3 w-3" />
                ) : isCompleted ? (
                  <Check className="h-3 w-3" />
                ) : (
                  step
                )}
              </span>

              <span>{label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
